import React, { useState, useEffect, useMemo } from "react";
import { Input, Button, Typography, Empty, Row, Col, Checkbox, Spin, Pagination } from "antd";
import { SearchOutlined, EnvironmentOutlined, HomeOutlined, ContactsOutlined } from "@ant-design/icons";
import { getParticipantesFiltrados } from "../../services/EcasParticipante/ecaParticipanteService";
import { debounce } from "lodash";
const { Text } = Typography;

export default function ParticipantSelector({
  participantesSeleccionados,
  onParticipantesChange,
  participantesExcluidos = [],
  isOpen = true,
  ecaOid,
  onCantidadParticipantesFiltrados,
  filtroDepartamentoInicial = null,
  filtroMunicipioInicial = null
}) {
  const [departamentoFiltro, setDepartamentoFiltro] = useState("all");
  const [municipioFiltro, setMunicipioFiltro] = useState("all");
  const [busqueda, setBusqueda] = useState("");
  const [participantes, setParticipantes] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;



  useEffect(() => {
    if (!isOpen) return;
    if (filtroDepartamentoInicial) {
      setDepartamentoFiltro(filtroDepartamentoInicial);
    }
    if (filtroMunicipioInicial) {
      setMunicipioFiltro(filtroMunicipioInicial);
    }
  }, [isOpen, filtroDepartamentoInicial, filtroMunicipioInicial]);

  useEffect(() => {
    if (!isOpen) return;

    const fetch = debounce(async () => {
      setLoading(true);
      try {
        const filtros = {
          departamentoId: departamentoFiltro !== "all" ? departamentoFiltro : undefined,
          municipioId: municipioFiltro !== "all" ? municipioFiltro : undefined,
          search: busqueda
        };

        if (ecaOid) {
          filtros.eca_oid = ecaOid;
        }
        const data = await getParticipantesFiltrados(filtros);
        setParticipantes(data);
        setCurrentPage(1);
      } catch (error) {
        console.error("Error al cargar participantes:", error);
      } finally {
        setLoading(false);
      }
    }, 300);
    fetch();
    return () => fetch.cancel();
  }, [departamentoFiltro, municipioFiltro, busqueda, isOpen, ecaOid]);

  const participantesFiltrados = useMemo(() => {
    const excluidosIds = new Set((participantesExcluidos || []).map((p) => p.oid));
    return participantes.filter((p) => !excluidosIds.has(p.oid));
  }, [participantes, participantesExcluidos]);

  useEffect(() => {
    if (typeof onCantidadParticipantesFiltrados === "function") {
      onCantidadParticipantesFiltrados(participantesFiltrados.length);
    }
  }, [participantesFiltrados]);

  const paginatedParticipantes = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return participantesFiltrados.slice(start, end);
  }, [participantesFiltrados, currentPage]);

  const handleSeleccionar = (participante, checked) => {
    onParticipantesChange(
      checked
        ? [...participantesSeleccionados, participante]
        : participantesSeleccionados.filter((p) => p.oid !== participante.oid)
    );
  };

  return (
    <Spin spinning={loading}>
      <Row gutter={12} style={{ marginBottom: 12 }}>
        <Col xs={24} md={12}>
          <Input
            placeholder="Buscar por nombre o cédula"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            prefix={<SearchOutlined />}
            allowClear
          />
        </Col>
        <Col xs={24} md={12}>
          <Button onClick={() => setBusqueda("")} style={{ width: "100%" }}>
            Limpiar búsqueda
          </Button>
        </Col>
      </Row>

      <Text type="secondary">
        Mostrando {participantesFiltrados.length} participantes
      </Text>

      <div style={{ maxHeight: 400,  marginTop: 10 }}>
        {paginatedParticipantes.length === 0 ? (
          <Empty description="No se encontraron participantes" />
        ) : (
          (() => {
            const gruposDeDos = [];
            for (let i = 0; i < paginatedParticipantes.length; i += 2) {
              gruposDeDos.push(paginatedParticipantes.slice(i, i + 2));
            }

            return gruposDeDos.map((grupo, index) => (
              <Row key={index} gutter={12} style={{ marginBottom: 8 }}>
                {grupo.map((p) => (
                  <Col key={p.oid} span={12}>
                    <div
                      style={{
                        display: "flex",
                        padding: 8,
                        border: "1px solid #eee",
                        borderRadius: 4,
                        marginBottom: 4
                      }}
                    >
                      <Checkbox
                        checked={participantesSeleccionados.some(
                          (sel) => sel.oid === p.oid
                        )}
                        onChange={(e) =>
                          handleSeleccionar(p, e.target.checked)
                        }
                      />
                      <div style={{ marginLeft: 12 }}>
                        <Text strong>{p.nombre_participante}</Text>
                        <div style={{ fontSize: 12, color: "#666" }}>
                          <HomeOutlined /> {p.departamento} |{" "}
                          <EnvironmentOutlined /> {p.municipio} |{" "}
                          <ContactsOutlined /> {p.numero_identificacion}
                        </div>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            ));
          })()
        )}
      </div>

      {participantesFiltrados.length > pageSize && (
        <div style={{ marginTop: 16, textAlign: "center" }}>
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={participantesFiltrados.length}
            onChange={setCurrentPage}
            showSizeChanger={false}
          />
        </div>
      )}
    </Spin>
  );
}